/**
 * Ollama connection store.
 *
 * Tracks whether the local Ollama service is installed/running and can start
 * it. Every status change is mirrored into `appStore.backendStatus`, so the
 * existing `StatusBar` indicator reflects it with no changes of its own.
 */
import { create } from "zustand";
import type { OllamaConnectionStatus } from "@/types";
import { checkOllamaStatus, startOllama } from "@/services/ai";
import { logger } from "@/services/logger";
import { useAppStore } from "./appStore";
import { useSettingsStore } from "./settingsStore";
import { useModelsStore } from "./modelsStore";

const log = logger.scope("ollama-store");

const BACKEND_STATUS_MAP: Record<OllamaConnectionStatus, "offline" | "starting" | "ready" | "error"> = {
  checking: "starting",
  "not-installed": "offline",
  "not-running": "offline",
  ready: "ready",
  error: "error",
};

interface OllamaState {
  status: OllamaConnectionStatus;
  installed: boolean;
  running: boolean;
  version: string | null;
  message: string | null;

  check: () => Promise<void>;
  start: () => Promise<void>;
}

export const useOllamaStore = create<OllamaState>()((set, get) => ({
  status: "checking",
  installed: false,
  running: false,
  version: null,
  message: null,

  check: async () => {
    set({ status: "checking" });
    useAppStore.getState().setBackendStatus(BACKEND_STATUS_MAP.checking);
    try {
      const baseUrl = useSettingsStore.getState().ollamaHost;
      const result = await checkOllamaStatus(baseUrl);
      const status: OllamaConnectionStatus = !result.installed
        ? "not-installed"
        : !result.running
          ? "not-running"
          : "ready";
      set({
        status,
        installed: result.installed,
        running: result.running,
        version: result.version,
        message: null,
      });
      useAppStore.getState().setBackendStatus(BACKEND_STATUS_MAP[status]);

      if (status === "ready" && useModelsStore.getState().models.length === 0) {
        void useModelsStore.getState().refresh();
      }
    } catch (err) {
      log.error("Failed to check Ollama status", err);
      set({
        status: "error",
        message: err instanceof Error ? err.message : "Couldn't check Ollama's status.",
      });
      useAppStore.getState().setBackendStatus(BACKEND_STATUS_MAP.error);
    }
  },

  start: async () => {
    try {
      await startOllama();
      useAppStore.getState().addToast({
        title: "Starting Ollama",
        description: "This can take a few seconds.",
        variant: "default",
      });
      setTimeout(() => void get().check(), 1500);
    } catch (err) {
      log.error("Failed to start Ollama", err);
      useAppStore.getState().addToast({
        title: "Couldn't start Ollama",
        description: err instanceof Error ? err.message : "Try starting it manually.",
        variant: "error",
      });
    }
  },
}));
